import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CloseIconBlock,
  SignOutHandler,
  UrlLinks,
  useDeviceType,
  DeviceTypes,
  NavigateConst,
  isGuest,
  isWellnessUser,
  shouldDisplayGetHealthyMenu,
  ANALYTICS_MESSAGES,
  SCREEN_NAMES,
  getAnalyticsBaseConfig,
  domainForSegment,
} from 'common';
import { ContactUsLink } from '../header/header';
import { GlobalContext } from 'context';
import { DashboardLinks } from 'pages';
import {
  Button,
  UserIconBlack,
  Accordion,
  ModalOverlayHamburger,
  ModalMultiUse,
  HamburgerIcon,
  Link,
  ButtonLink,
} from '@sentara-health/optimahealth_web';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
/**
 * Hamburger menu props
 */
interface HamburgerMenuProps {
  showLogout: boolean;
}
/**
 * Shows logout
 * @param { showLogout }
 * @returns
 */

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ showLogout }) => {
  const { t, i18n } = useTranslation();
  const signOut = SignOutHandler();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === DeviceTypes.DESKTOP;
  const shouldShowIcon = true;
  const {
    getFeature,
    loginMID,
    ssoEndPoints,
    logging,
  } = useContext(GlobalContext);
  const {
    benefitsUrl,
    paymentCalims,
    doctorsMedication,
    getHealthy,
    memberProfileUrl,
    guestWellnessUrl,
    guestBenefitsUrl,
  }: any = UrlLinks();
  
  const profileUrl = window.location.pathname !== NavigateConst.profile; 
  // Initialize navigation function
  const navigate = useNavigate();
  const [loggedOutModal, setLoggedOutModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    i18n?.language === 'en-US' ? 'en' : 'es'
  );
  const [guestValue, setGuestValue] = useState<boolean>(false);
  const [wellnessValue, setWellnessValue] = useState<boolean>(false);
  // Using the useEffect hook to perform an operation after the component has been rendered
  useEffect(() => {
    // Load saved language preference
    const savedLang = localStorage.getItem('userLanguagePreference');
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
    setSelectedLanguage(i18n?.language === 'en-US' ? 'en' : 'es');

    // Immediately invoked async function within the useEffect hook
    (async () => {
      // Fetching the decrypted user details
      const isGuestUser = await isGuest();
      const isWellness = await isWellnessUser();
      setGuestValue(isGuestUser);
      setWellnessValue(isWellness);
    })();
  }, [i18n]);

  /**
   * Opens modal
   */
  function openModal() {
    setIsOpen(true);
  }
  /**
   * Closes modal
   */
  function closeModal() {
    setIsOpen(false);
  }
  function GetMemberHomeUrl() {
    return ssoEndPoints?.memberHome;
  }
  const handleAccordionToggleItem = (index: number) => {
    return index;
  };

  const trackLanguageSelection = (
    contentId: number,
    contentTitle: string,
    previousLanguage: string,
    newLanguage: string
  ) => {
    contentListingItemClicked({
      segment: `${domainForSegment}${globalThis.location.pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [
            {
              contentId: contentId as unknown as string,
              contentTitle,
              itemPosition: 1,
              ...({ previousLanguage, newLanguage } as Record<string, string>),
            },
          ],
        },
      },
    });
  };

  const handleLanguageAccordionToggleItem = (index: number) => {
    const lang = index === 0 ? 'en-US' : 'es';
    const currentLanguage = i18n?.language === 'en-US' ? 'en-US' : 'es';
    const isSameLanguage = currentLanguage === lang;
    localStorage.setItem('userLanguagePreference', lang);
    if (i18n?.changeLanguage) {
      i18n.changeLanguage(lang);
    }
    setSelectedLanguage(index === 0 ? 'en' : 'es');

    if (!isSameLanguage) {
      trackLanguageSelection(
        index === 0 ? 402 : 401,
        index === 0 ? 'Language_english_click' : 'Language_spanish_click',
        index === 0 ? 'spanish' : 'english',
        index === 0 ? 'english' : 'spanish'
      );
    }

    // AppInsights trace — language option selected
    logging?.trace({
      additionalDetails: getAnalyticsBaseConfig({
        languageSelected: index === 0 ? 'English' : 'Spanish',
      }),
      message: ANALYTICS_MESSAGES.LANGUAGE_OPTION_SELECTED,
      screenName: SCREEN_NAMES.HEADER,
    });

    setIsOpen(false);
    return index;
  };
  const memberHomeUrl = GetMemberHomeUrl();
  const { contentListingItemClicked } = useAdobeAnalytics();

  return (
    <>
      {!isDesktop && (
        <>
          <Button
            variant="icon_btn"
            className="ms-4 h-100 d-flex align-items-center"
            type="button"
            aria-label="Toggle navigation"
            onClick={openModal}
            data-testid="hamburgerIconButton"
          >
            <HamburgerIcon />
          </Button>
          <ModalOverlayHamburger
            isOpen={isOpen}
            onRequestClose={closeModal}
            data-testid="hamburgerClose"
            contentLabel={t('common.overlayInfo')}
          >
            <div className="hamburger-menu-overlay">
              <div className="pt-4 p-3 pb-2">
                <Button
                  variant="icon_btn"
                  iconLeft={<CloseIconBlock />}
                  aria-label="Close"
                  type="button"
                  data-testid="hamburgerBtnClose"
                  onClick={closeModal}
                >
                  {t('common.close')}
                </Button>
              </div>
              <hr className="border-w-2" />
              <div className="p-3">
                {showLogout && (
                  <div className="d-grid mb-4">
                    <Button
                      dataTestId={'submitButton'}
                      iconLeft={<UserIconBlack />}
                      variant="secondary_btn"
                      onClick={() => {
                        setLoggedOutModal(true);
                      }}
                    >
                      {t('common.logout')}
                    </Button>
                  </div>
                )}
                <div className="d-flex align-items-center flex-no-wrap">
                  <ContactUsLink
                    memberID={loginMID}
                    showIcon={shouldShowIcon}
                    showFAQLink={!showLogout && getFeature?.FAQs}
                    onItemClick={() => setIsOpen(false)}
                  />
                </div>
              </div>
              {getFeature?.MultiLanguage && (
                <>
                  <hr className="border-w-2" />
                  <div className="accordion-hamburgermenu">
                    <Accordion
                      initialActiveIndex={1}
                      id="language_menu"
                      items={[
                        {
                          accIcon: <></>,
                          id: 'language_options',
                          content: (
                            <div className="d-flex flex-column gap-2 align-items-start ps-4">
                              <ButtonLink
                                dataTestId="hamburger-lang-en"
                                buttonText="English"
                                isHeader
                                onClick={() =>
                                  handleLanguageAccordionToggleItem(0)
                                }
                                aria-pressed={selectedLanguage === 'en'}
                              />
                              <ButtonLink
                                dataTestId="hamburger-lang-es"
                                buttonText="Español"
                                isHeader
                                onClick={() =>
                                  handleLanguageAccordionToggleItem(1)
                                }
                                aria-pressed={selectedLanguage === 'es'}
                              />
                            </div>
                          ),
                          title:
                            selectedLanguage === 'en' ? 'English' : 'Español',
                        },
                      ]}
                    />
                  </div>
                </>
              )}
              <hr className="border-w-2" />
              {showLogout && (
                <div className="pb-4">
                  <div className="accordion-hamburgermenu">
                    <Accordion
                      onToggleItem={handleAccordionToggleItem}
                      initialActiveIndex={0}
                      id="hamburger_menu_mob"
                      items={[
                        getFeature?.MemberAccount && {
                          accIcon: <></>,
                          id: 'member_profile_mob',
                          content: (
                            <>
                              {profileUrl && memberProfileUrl.length > 0 && (
                                <DashboardLinks
                                  data={memberProfileUrl}
                                  showContent={getFeature?.MemberAccount}
                                  closeModal={closeModal}
                                />
                              )}
                            </>
                          ),
                          title: t('common.memberAccount'),
                        },
                        //Benefist coverage for for member
                        getFeature?.BenefitsCoverage &&
                        !guestValue && {
                            content: (
                              <>
                                {benefitsUrl.length > 0 && (
                                  <DashboardLinks
                                    data={benefitsUrl}
                                    showContent={getFeature?.BenefitsCoverage}
                                    closeModal={closeModal}
                                  />
                                )}
                              </>
                            ),
                            title: t('common.benefitsCoverage'),
                            id: 'benefits_coverage_mob',
                          },
                        //To show only enroll and benefits link in side menu for guest user
                        guestValue && {
                          id: 'benefit_coverage_guest_mob',
                          content: (
                            <>
                              {guestBenefitsUrl.length > 0 && (
                                <DashboardLinks
                                  data={guestBenefitsUrl}
                                  showContent={getFeature?.BenefitsCoverage}
                                  closeModal={closeModal}
                                />
                              )}
                            </>
                          ),
                          title: t('common.benefitsCoverage'),
                        },

                        //To show the getHealthy link in side menu
                        getFeature?.WellnessPrograms &&
                          wellnessValue && {
                            content: (
                              <>
                                {guestWellnessUrl.length > 0 && (
                                  <DashboardLinks
                                    data={guestWellnessUrl}
                                    closeModal={closeModal}
                                  />
                                )}
                              </>
                            ),
                            title: t('common.getHealthy'),
                            id: 'getHealthy_mob',
                          },

                        //To show the PaymentsBillingAndClaims links in side menu
                        getFeature?.PaymentsBillingAndClaims &&
                        !guestValue && {
                            content: (
                              <>
                                {paymentCalims.length > 0 && (
                                  <DashboardLinks
                                    data={paymentCalims}
                                    closeModal={closeModal}
                                  />
                                )}
                              </>
                            ),
                            title: t('common.paymentsBillingsClaims'),
                            id: 'payments_billing_mob',
                          },
                        //To show the docotor and Medication link in side menu
                        getFeature?.DoctorAndMedications &&
                        !guestValue && {
                            content: (
                              <>
                                {profileUrl && (
                                  <DashboardLinks
                                    data={doctorsMedication}
                                    closeModal={closeModal}
                                  />
                                )}
                              </>
                            ),
                            title: t('common.doctorsMedication'),
                            id: 'doctor_medication_mob',
                          },
                        //To show the gethealthy link in side menu
                        shouldDisplayGetHealthyMenu(
                          guestValue,
                          false,
                          '',
                          getHealthy,
                          getFeature?.GetHealthy
                        ) && {
                          content: (
                            <DashboardLinks
                              data={getHealthy}
                              closeModal={closeModal}
                            />
                          ),
                          title: t('common.getHealthy'),
                          id: 'get_healthy_mob',
                        },
                      ].filter((item) => item)}
                      className="px-3"
                    />
                  </div>
                  {(getFeature?.BenefitsCoverage ||
                    getFeature?.PaymentsBillingAndClaims ||
                    getFeature?.DoctorAndMedications ||
                    getFeature?.GetHealthy) && <hr className="border-w-2" />}
                  <ul className="hamburger-nav-two px-3">
                    <li>
                      {getFeature?.MyProfile && !guestValue && (
                        <Link
                          to={NavigateConst.profile}
                          data-testid="profileButton"
                          onClick={() => {
                            setIsOpen(false);
                          }}
                        >
                          {t('common.profile')}
                        </Link>
                      )}
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </ModalOverlayHamburger>
          {/* Modal to confirm logout action */}
          <ModalMultiUse
            isOpen={loggedOutModal}
            onClose={() => setLoggedOutModal(false)}
            body={t('common.logOutModalDescription')}
            bodyClass="text-center"
            footer={
              <>
                <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
                  <Button
                    dataTestId="confirmLogoutButton"
                    variant="primary_btn"
                    className="btn-md-height w-100"
                    onClick={() => {
                      signOut();
                      setLoggedOutModal(false);
                      contentListingItemClicked({
                        segment: `${domainForSegment}/${globalThis.location.pathname}`,
                        contentListing: {
                          eventType: "Content Listing Item Clicked",
                          listing: {
                            content: [
                              { contentId: ANALYTICS_MESSAGES.POP_USER_SIGN_OUT, contentTitle: 'Sign Out', itemPosition: 1 },
                            ],
                          },
                        },
                      });
                    }}
                  >
                    {t('common.logout')}
                  </Button>
                </div>
                <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
                  <Button
                    dataTestId="cancelLogoutButton"
                    variant="secondary_btn"
                    className="btn-md-height w-100"
                    onClick={() => {
                      setLoggedOutModal(false);
                      contentListingItemClicked({
                        segment: `${domainForSegment}/${globalThis.location.pathname}`,
                        contentListing: {
                          eventType: 'Content Listing Item Clicked',
                          listing: {
                            content: [
                              { contentId: ANALYTICS_MESSAGES.POP_USER_SIGN_OUT_CANCEL, contentTitle: 'Cancel', itemPosition: 1 },
                            ],
                          },
                        },
                      });
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </>
            }
            variant="defaultNoPbBody"
            data-testid="logoutConfirmationModal"
          />
        </>
      )}
    </>
  );
};
export default HamburgerMenu;